import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getSpotifyEmbedData,
  getTikTokVideoData,
  getTwitterStatusData,
  getYouTubeVideoId,
  type BlogBlock,
  type CollectionAdminItem,
  type Locale,
} from '@bailafm/shared';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Calendar, ExternalLink, GripVertical, Heading, Image as ImageIcon, Library, Music, Plus, Trash2, Twitter, Type, User, Youtube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BlogImageField } from '@/components/blog/BlogImageField';
import { RichTextEditor } from '@/components/blog/RichTextEditor';
import { artistsApi, blogApi, collectionsApi, eventsApi, type AdminEventSearchItem } from '@/lib/api';
import type { PixelCrop } from '@/lib/crop-image';
import { cn } from '@/lib/utils';

interface BlogBlocksEditorProps {
  postId: string;
  blocks: BlogBlock[];
  activeLocale: Locale;
  onChange(blocks: BlogBlock[]): void;
}

function newId(): string {
  return crypto.randomUUID();
}

// lucide-react no trae el logo de TikTok; SVG propio (mismo tamaño/uso
// que los iconos de lucide, `size-4` vía className).
function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  );
}

interface AddBlockMenuProps {
  onAdd(type: BlogBlock['type']): void;
  align?: 'start' | 'center' | 'end';
}

/**
 * Trigger único "+ Añadir bloque" con los 11 tipos agrupados por
 * categoría. Sustituye la fila de botones sueltos (no escalaba: cada
 * bloque nuevo sumaba un botón más y quedaba apelotonado). Se usa dos
 * veces (arriba del listado y debajo del último bloque) para no obligar
 * a subir el scroll cada vez que se quiere añadir otro.
 */
function AddBlockMenu({ onAdd, align = 'end' }: AddBlockMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <Plus className="size-4" aria-hidden /> Añadir bloque
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-56">
        <DropdownMenuLabel>Contenido</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => onAdd('heading')}>
          <Heading className="size-4" aria-hidden /> Cabecera
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onAdd('text')}>
          <Type className="size-4" aria-hidden /> Texto
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onAdd('image')}>
          <ImageIcon className="size-4" aria-hidden /> Imagen
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Referencias</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => onAdd('collection')}>
          <Library className="size-4" aria-hidden /> Colección
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onAdd('artist')}>
          <User className="size-4" aria-hidden /> Artista
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onAdd('event')}>
          <Calendar className="size-4" aria-hidden /> Evento
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onAdd('cta')}>
          <ExternalLink className="size-4" aria-hidden /> CTA
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Embeds</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => onAdd('youtube')}>
          <Youtube className="size-4" aria-hidden /> YouTube
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onAdd('spotify')}>
          <Music className="size-4" aria-hidden /> Spotify
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onAdd('twitter')}>
          <Twitter className="size-4" aria-hidden /> X
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onAdd('tiktok')}>
          <TikTokIcon className="size-4" /> TikTok
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Editor del cuerpo por bloques (estilo WordPress): añadir cabecera /
 * texto / imagen, reordenar (drag&drop) y borrar. Los textos se editan en
 * el idioma activo (selector a nivel de página). Las imágenes de bloque
 * se suben al confirmar el recorte (la entrada ya tiene id).
 */
export function BlogBlocksEditor({
  postId,
  blocks,
  activeLocale,
  onChange,
}: BlogBlocksEditorProps) {
  const [collections, setCollections] = useState<CollectionAdminItem[]>([]);
  const [collectionsLoading, setCollectionsLoading] = useState(true);
  const [lastAddedId, setLastAddedId] = useState<string | null>(null);

  // Feedback al añadir un bloque: lo trae a la vista (el usuario puede
  // haber pulsado "Añadir bloque" desde el trigger de arriba con la
  // lista ya larga) y lo resalta un momento para dejar claro que se
  // acaba de crear. El resaltado se apaga solo a los ~1.6s.
  useEffect(() => {
    if (!lastAddedId) return;
    document
      .getElementById(`blog-block-${lastAddedId}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setLastAddedId(null), 1600);
    return () => clearTimeout(timer);
  }, [lastAddedId]);

  useEffect(() => {
    let cancelled = false;
    setCollectionsLoading(true);
    // `listAll()` (no `list()`, que ahora pagina): el selector necesita
    // todas las colecciones disponibles para el bloque, no una página.
    collectionsApi
      .listAll()
      .then((items) => {
        if (!cancelled) setCollections(items);
      })
      .catch(() => {
        // Si falla la carga, el selector se mostrará vacío
      })
      .finally(() => {
        if (!cancelled) setCollectionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const addBlock = useCallback(
    (type: BlogBlock['type']) => {
      const id = newId();
      const block = ((): BlogBlock => {
        switch (type) {
          case 'heading': return { id, type: 'heading', text: {} };
          case 'text':    return { id, type: 'text', html: {} };
          case 'image':   return { id, type: 'image', alt: {} };
          case 'collection': return { id, type: 'collection', collectionSlug: '' };
          case 'artist':  return { id, type: 'artist', artistSlug: '' };
          case 'event':   return { id, type: 'event', eventSlug: '' };
          case 'cta':     return { id, type: 'cta', text: {}, url: '', newTab: false };
          case 'youtube': return { id, type: 'youtube', url: '' };
          case 'spotify': return { id, type: 'spotify', url: '' };
          case 'twitter': return { id, type: 'twitter', url: '' };
          case 'tiktok': return { id, type: 'tiktok', url: '' };
        }
      })();
      onChange([...blocks, block]);
      setLastAddedId(id);
    },
    [blocks, onChange],
  );

  const updateBlock = useCallback(
    (id: string, next: BlogBlock) => {
      onChange(blocks.map((b) => (b.id === id ? next : b)));
    },
    [blocks, onChange],
  );

  const removeBlock = useCallback(
    (id: string) => {
      onChange(blocks.filter((b) => b.id !== id));
    },
    [blocks, onChange],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = blocks.findIndex((b) => b.id === active.id);
      const newIndex = blocks.findIndex((b) => b.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      onChange(arrayMove(blocks, oldIndex, newIndex));
    },
    [blocks, onChange],
  );

  return (
    <section className="space-y-3">
      <header className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Contenido
        </h3>
        <AddBlockMenu onAdd={addBlock} align="end" />
      </header>

      {blocks.length === 0 ? (
        <p className="rounded-md border border-dashed bg-card p-6 text-center text-sm text-muted-foreground">
          Aún no hay bloques. Añade una cabecera, un texto o una imagen.
        </p>
      ) : (
        <>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
              <ul className="space-y-2">
                {blocks.map((block) => (
                  <SortableBlockRow
                    key={block.id}
                    postId={postId}
                    block={block}
                    activeLocale={activeLocale}
                    collections={collections}
                    collectionsLoading={collectionsLoading}
                    highlighted={block.id === lastAddedId}
                    onChange={(next) => updateBlock(block.id, next)}
                    onRemove={() => removeBlock(block.id)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
          <div className="flex justify-center pt-1">
            <AddBlockMenu onAdd={addBlock} align="center" />
          </div>
        </>
      )}
    </section>
  );
}

interface SortableBlockRowProps {
  postId: string;
  block: BlogBlock;
  activeLocale: Locale;
  collections: CollectionAdminItem[];
  collectionsLoading: boolean;
  highlighted: boolean;
  onChange(next: BlogBlock): void;
  onRemove(): void;
}

const BLOCK_LABELS: Record<BlogBlock['type'], string> = {
  heading: 'Cabecera (h3)',
  text: 'Texto',
  image: 'Imagen',
  collection: 'Colección',
  artist: 'Artista',
  event: 'Evento',
  cta: 'CTA',
  youtube: 'YouTube',
  spotify: 'Spotify',
  twitter: 'X (Twitter)',
  tiktok: 'TikTok',
};

function SortableBlockRow({
  postId,
  block,
  activeLocale,
  collections,
  collectionsLoading,
  highlighted,
  onChange,
  onRemove,
}: SortableBlockRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li id={`blog-block-${block.id}`} ref={setNodeRef} style={style} className={cn(isDragging && 'z-10')}>
      <article
        className={cn(
          'rounded-lg border bg-card p-3 transition-shadow duration-700',
          isDragging && 'shadow-lg ring-2 ring-foreground/20',
          highlighted && 'ring-2 ring-ring ring-offset-2 ring-offset-background',
        )}
      >
        <header className="mb-2 flex items-center gap-2">
          <button
            type="button"
            aria-label="Arrastra para reordenar"
            className="flex size-7 shrink-0 cursor-grab touch-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="size-4" aria-hidden />
          </button>
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {BLOCK_LABELS[block.type]}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Borrar bloque"
            onClick={onRemove}
            className="ml-auto size-7 text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="size-4" aria-hidden />
          </Button>
        </header>

        <BlockBody
          postId={postId}
          block={block}
          activeLocale={activeLocale}
          collections={collections}
          collectionsLoading={collectionsLoading}
          onChange={onChange}
        />
      </article>
    </li>
  );
}

function BlockBody({ postId, block, activeLocale, collections, collectionsLoading, onChange }: Omit<SortableBlockRowProps, 'onRemove' | 'highlighted'>) {
  if (block.type === 'heading') {
    return (
      <Input
        value={block.text[activeLocale] ?? ''}
        onChange={(e) =>
          onChange({
            ...block,
            text: { ...block.text, [activeLocale]: e.target.value || undefined },
          })
        }
        placeholder="Texto de la cabecera…"
        autoComplete="off"
      />
    );
  }

  if (block.type === 'text') {
    return (
      <RichTextEditor
        key={`${block.id}-${activeLocale}`}
        value={block.html[activeLocale] ?? ''}
        onChange={(html) =>
          onChange({ ...block, html: { ...block.html, [activeLocale]: html || undefined } })
        }
      />
    );
  }

  if (block.type === 'collection') {
    const selected = collections.find((c) => c.slug === block.collectionSlug);
    return (
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor={`collection-${block.id}`}>Colección</Label>
          {collectionsLoading ? (
            <p className="text-sm text-muted-foreground">Cargando colecciones…</p>
          ) : (
            <select
              id={`collection-${block.id}`}
              value={block.collectionSlug}
              onChange={(e) =>
                onChange({
                  ...block,
                  collectionSlug: e.target.value,
                })
              }
              className={cn(
                'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              )}
            >
              <option value="">Seleccionar colección…</option>
              {collections.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.label.es ?? c.label.ca ?? c.label.en ?? c.slug}
                </option>
              ))}
            </select>
          )}
          {selected && (
            <p className="text-xs text-muted-foreground">
              Slug: <code>{selected.slug}</code>
              {selected.images?.page && ' · Con imagen'}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (block.type === 'artist') {
    return (
      <ArtistBlockBody
        block={block}
        onChange={onChange}
      />
    );
  }

  if (block.type === 'event') {
    return (
      <EventBlockBody
        block={block}
        onChange={onChange}
      />
    );
  }

  if (block.type === 'cta') {
    return (
      <CtaBlockBody
        block={block}
        activeLocale={activeLocale}
        onChange={onChange}
      />
    );
  }

  if (block.type === 'youtube') {
    return (
      <YouTubeBlockBody
        block={block}
        onChange={onChange}
      />
    );
  }

  if (block.type === 'spotify') {
    return (
      <SpotifyBlockBody
        block={block}
        onChange={onChange}
      />
    );
  }

  if (block.type === 'twitter') {
    return (
      <TwitterBlockBody
        block={block}
        onChange={onChange}
      />
    );
  }

  if (block.type === 'tiktok') {
    return (
      <TikTokBlockBody
        block={block}
        onChange={onChange}
      />
    );
  }

  // Imagen.

  // Imagen.
  const uploadContent = async (file: File, crop: PixelCrop): Promise<string> => {
    const ref = await blogApi.uploadImage(postId, 'content', file, crop);
    onChange({ ...block, image: ref });
    return ref.url;
  };

  return (
    <div className="space-y-3">
      <div className="max-w-xs">
        <BlogImageField
          title="Imagen del cuerpo"
          hint="Proporción libre · se limita el ancho y se optimiza"
          existingUrl={block.image?.url}
          onUpload={uploadContent}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`alt-${block.id}`}>Texto alternativo (alt)</Label>
        <Input
          id={`alt-${block.id}`}
          value={block.alt[activeLocale] ?? ''}
          onChange={(e) =>
            onChange({
              ...block,
              alt: { ...block.alt, [activeLocale]: e.target.value || undefined },
            })
          }
          placeholder="Describe la imagen…"
          autoComplete="off"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`caption-${block.id}`}>Pie de imagen (opcional)</Label>
        <Input
          id={`caption-${block.id}`}
          value={block.caption?.[activeLocale] ?? ''}
          onChange={(e) =>
            onChange({
              ...block,
              caption: { ...(block.caption ?? {}), [activeLocale]: e.target.value || undefined },
            })
          }
          placeholder="Pie de foto…"
          autoComplete="off"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`img-href-${block.id}`}>Enlace de la imagen (opcional)</Label>
        <Input
          id={`img-href-${block.id}`}
          value={block.href ?? ''}
          onChange={(e) =>
            onChange({ ...block, href: e.target.value || undefined })
          }
          placeholder="https://… o /ruta-interna"
          autoComplete="off"
        />
      </div>
      {block.href && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={block.newTab ?? false}
            onChange={(e) => onChange({ ...block, newTab: e.target.checked })}
            className="rounded border-input text-foreground focus:ring-ring"
          />
          Abrir en nueva pestaña
        </label>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// YouTubeBlockBody — editor de bloque YouTube (URL + thumbnail preview).
// ---------------------------------------------------------------------------

interface YouTubeBlockBodyProps {
  block: BlogBlock & { type: 'youtube' };
  onChange(next: BlogBlock): void;
}

function YouTubeBlockBody({ block, onChange }: YouTubeBlockBodyProps) {
  const videoId = getYouTubeVideoId(block.url);
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor={`yt-url-${block.id}`}>URL del vídeo de YouTube</Label>
        <Input
          id={`yt-url-${block.id}`}
          value={block.url}
          onChange={(e) => onChange({ ...block, url: e.target.value })}
          placeholder="https://www.youtube.com/watch?v=…"
          autoComplete="off"
        />
      </div>
      {videoId && (
        <div className="mx-auto w-1/2 overflow-hidden rounded-md border">
          <img
            src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
            alt="Vista previa del vídeo"
            className="w-full aspect-video object-cover"
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SpotifyBlockBody — editor de bloque Spotify (URL + embed preview).
// ---------------------------------------------------------------------------

interface SpotifyBlockBodyProps {
  block: BlogBlock & { type: 'spotify' };
  onChange(next: BlogBlock): void;
}

function SpotifyBlockBody({ block, onChange }: SpotifyBlockBodyProps) {
  const embedData = getSpotifyEmbedData(block.url);
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor={`sp-url-${block.id}`}>URL de Spotify</Label>
        <Input
          id={`sp-url-${block.id}`}
          value={block.url}
          onChange={(e) => onChange({ ...block, url: e.target.value })}
          placeholder="https://open.spotify.com/track/…"
          autoComplete="off"
        />
      </div>
      {embedData && (
        <div className="overflow-hidden rounded-md border">
          <iframe
            src={embedData.embedUrl}
            width="100%"
            height={152}
            allow="encrypted-media"
            className="block"
            title="Vista previa de Spotify"
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TwitterBlockBody — editor de bloque X/Twitter (URL + preview embebida).
// El admin es un panel interno autenticado, sin CMP: a diferencia de la
// web (que hace click-to-load para no fijar cookies de terceros sin
// consentimiento), aquí la vista previa carga directa.
// ---------------------------------------------------------------------------

interface TwitterBlockBodyProps {
  block: BlogBlock & { type: 'twitter' };
  onChange(next: BlogBlock): void;
}

declare global {
  interface Window {
    twttr?: { widgets: { load(el?: HTMLElement): void } };
  }
}

function TwitterBlockBody({ block, onChange }: TwitterBlockBodyProps) {
  const data = useMemo(() => getTwitterStatusData(block.url), [block.url]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!data) return;
    if (window.twttr) {
      window.twttr.widgets.load(containerRef.current ?? undefined);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://platform.x.com/widgets.js';
    script.async = true;
    script.onload = () => window.twttr?.widgets.load(containerRef.current ?? undefined);
    document.body.appendChild(script);
  }, [data]);

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor={`tw-url-${block.id}`}>URL de la publicación en X</Label>
        <Input
          id={`tw-url-${block.id}`}
          value={block.url}
          onChange={(e) => onChange({ ...block, url: e.target.value })}
          placeholder="https://x.com/usuario/status/…"
          autoComplete="off"
        />
      </div>
      {data ? (
        <div ref={containerRef} className="mx-auto max-w-xs overflow-hidden rounded-md border p-2">
          <blockquote className="twitter-tweet">
            <a href={data.canonicalUrl} />
          </blockquote>
        </div>
      ) : (
        block.url && (
          <p className="text-xs text-destructive">
            URL no reconocida. Debe ser un enlace a un status de X (x.com o twitter.com).
          </p>
        )
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TikTokBlockBody — editor de bloque TikTok (URL + preview embebida).
// ---------------------------------------------------------------------------

interface TikTokBlockBodyProps {
  block: BlogBlock & { type: 'tiktok' };
  onChange(next: BlogBlock): void;
}

function TikTokBlockBody({ block, onChange }: TikTokBlockBodyProps) {
  const data = useMemo(() => getTikTokVideoData(block.url), [block.url]);

  useEffect(() => {
    if (!data) return;
    // El script de TikTok no expone una API pública de re-escaneo (a
    // diferencia de `twttr.widgets.load`); volver a inyectarlo es el
    // workaround estándar para procesar un blockquote añadido después
    // de la primera carga.
    const script = document.createElement('script');
    script.src = 'https://www.tiktok.com/embed.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, [data]);

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor={`tt-url-${block.id}`}>URL del vídeo de TikTok</Label>
        <Input
          id={`tt-url-${block.id}`}
          value={block.url}
          onChange={(e) => onChange({ ...block, url: e.target.value })}
          placeholder="https://www.tiktok.com/@usuario/video/…"
          autoComplete="off"
        />
      </div>
      {data ? (
        <div className="mx-auto max-w-xs overflow-hidden rounded-md border">
          <blockquote
            className="tiktok-embed"
            cite={block.url}
            data-video-id={data.videoId}
            style={{ maxWidth: 605, minWidth: 325 }}
          >
            <section />
          </blockquote>
        </div>
      ) : (
        block.url && (
          <p className="text-xs text-destructive">
            URL no reconocida. Debe ser un enlace a un vídeo de TikTok (formato /@usuario/video/ID).
          </p>
        )
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CtaBlockBody — editor de bloque CTA (texto + URL).
// ---------------------------------------------------------------------------

interface CtaBlockBodyProps {
  block: BlogBlock & { type: 'cta' };
  activeLocale: Locale;
  onChange(next: BlogBlock): void;
}

function CtaBlockBody({ block, activeLocale, onChange }: CtaBlockBodyProps) {
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor={`cta-text-${block.id}`}>Texto del botón</Label>
        <Input
          id={`cta-text-${block.id}`}
          value={block.text[activeLocale] ?? ''}
          onChange={(e) =>
            onChange({
              ...block,
              text: { ...block.text, [activeLocale]: e.target.value || undefined },
            })
          }
          placeholder="Ej: Ver oferta, Solicitar demo…"
          autoComplete="off"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`cta-url-${block.id}`}>URL de destino</Label>
        <Input
          id={`cta-url-${block.id}`}
          value={block.url}
          onChange={(e) =>
            onChange({ ...block, url: e.target.value })
          }
          placeholder="https://… o /ruta-interna"
          autoComplete="off"
        />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={block.newTab ?? false}
          onChange={(e) =>
            onChange({ ...block, newTab: e.target.checked })
          }
          className="rounded border-input text-foreground focus:ring-ring"
        />
        Abrir en nueva pestaña
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ArtistBlockBody — buscador y selector de artista.
// ---------------------------------------------------------------------------

interface ArtistBlockBodyProps {
  block: BlogBlock & { type: 'artist' };
  onChange(next: BlogBlock): void;
}

// ---------------------------------------------------------------------------
// EventBlockBody — buscador y selector de evento.
// ---------------------------------------------------------------------------

interface EventBlockBodyProps {
  block: BlogBlock & { type: 'event' };
  onChange(next: BlogBlock): void;
}

type EventLabel = { slug: string; title: string; city?: string };

function EventBlockBody({ block, onChange }: EventBlockBodyProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<{ id: string; title: string; slug?: string; city?: string; startAt?: string }[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  // Label del evento seleccionado, resuelto por slug. Sin esto, al abrir
  // un post ya guardado el chip mostraría el slug crudo (el bloque solo
  // persiste el slug), hasta que el editor volviera a buscar.
  const [hydrated, setHydrated] = useState<EventLabel | null>(null);

  // Efecto de búsqueda con debounce
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await eventsApi.search({ q: query.trim() });
        setResults(response.items.map((e: AdminEventSearchItem) => ({
          id: e.id,
          title: e.title,
          slug: e.slug,
          city: e.city,
          startAt: e.startAt,
        })));
        setShowResults(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const selectedSlug = block.eventSlug;

  // Hidrata el label del evento seleccionado por slug (catálogo cacheado
  // del Core). Degrada al slug crudo si el Core no lo resuelve.
  useEffect(() => {
    if (!selectedSlug) return;
    let cancelled = false;
    eventsApi
      .bySlug(selectedSlug)
      .then((item) => {
        if (!cancelled && item) {
          setHydrated({ slug: selectedSlug, title: item.title, city: item.city });
        }
      })
      .catch(() => {
        // Degrada al slug crudo.
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSlug]);

  const selectedEvent: EventLabel | { slug?: string; title: string; city?: string } | undefined =
    results.find((e) => e.slug === selectedSlug) ??
    (hydrated?.slug === selectedSlug ? hydrated : undefined);

  const handleSelect = (item: { id: string; title: string; slug?: string; city?: string }) => {
    const slug = item.slug ?? item.id;
    onChange({ ...block, eventSlug: slug });
    // Siembra el label al instante para no parpadear al slug tras limpiar
    // los resultados de búsqueda.
    setHydrated({ slug, title: item.title, city: item.city });
    setShowResults(false);
    setQuery('');
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor={`event-search-${block.id}`}>Evento</Label>
        {selectedSlug && !showResults ? (
          <div className="flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm">
            <span className="flex-1 font-medium">{selectedEvent?.title ?? selectedSlug}</span>
            {selectedEvent?.city && (
              <span className="text-xs text-muted-foreground">{selectedEvent.city}</span>
            )}
            <button
              type="button"
              onClick={() => {
                onChange({ ...block, eventSlug: '' });
                setQuery('');
              }}
              className="text-xs text-muted-foreground underline hover:text-foreground"
            >
              Cambiar
            </button>
          </div>
        ) : (
          <div className="relative">
            <Input
              id={`event-search-${block.id}`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar evento por nombre…"
              autoComplete="off"
              onFocus={() => results.length > 0 && setShowResults(true)}
              onBlur={() => setTimeout(() => setShowResults(false), 200)}
            />
            {searching && (
              <p className="mt-1 text-xs text-muted-foreground">Buscando…</p>
            )}
            {showResults && results.length > 0 && (
              <ul className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-md border bg-background shadow-lg">
                {results.map((e) => (
                  <li
                    key={e.id}
                    onMouseDown={() => handleSelect(e)}
                    className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-muted"
                  >
                    <span className="font-medium">{e.title}</span>
                    {e.city && (
                      <span className="text-xs text-muted-foreground">{e.city}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {showResults && query.trim().length >= 2 && results.length === 0 && !searching && (
              <p className="mt-1 text-xs text-muted-foreground">Sin resultados</p>
            )}
          </div>
        )}
      </div>
      {selectedSlug && (
        <p className="text-xs text-muted-foreground">
          Slug: <code>{selectedSlug}</code>
        </p>
      )}
    </div>
  );
}

type ArtistLabel = { slug: string; name: string; avatarUrl?: string };

function ArtistBlockBody({ block, onChange }: ArtistBlockBodyProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<{ id: string; name: string; slug?: string; avatarUrl?: string }[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  // Label del artista seleccionado, resuelto por slug (ver EventBlockBody).
  const [hydrated, setHydrated] = useState<ArtistLabel | null>(null);

  // Efecto de búsqueda con debounce
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const items = await artistsApi.search(query.trim());
        setResults(items);
        setShowResults(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const selectedSlug = block.artistSlug;

  // Hidrata el label del artista seleccionado por slug. El buscador de
  // artistas no resuelve por slug, así que pedimos la lista (cacheada en
  // el Core) y filtramos. Degrada al slug crudo si no aparece.
  useEffect(() => {
    if (!selectedSlug) return;
    let cancelled = false;
    artistsApi
      .search('')
      .then((items) => {
        if (cancelled) return;
        const found = items.find((a) => a.slug === selectedSlug);
        if (found) {
          setHydrated({ slug: selectedSlug, name: found.name, avatarUrl: found.avatarUrl });
        }
      })
      .catch(() => {
        // Degrada al slug crudo.
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSlug]);

  const selectedArtist: ArtistLabel | { slug?: string; name: string; avatarUrl?: string } | undefined =
    results.find((a) => a.slug === selectedSlug) ??
    (hydrated?.slug === selectedSlug ? hydrated : undefined);

  const handleSelect = (item: { id: string; name: string; slug?: string; avatarUrl?: string }) => {
    const slug = item.slug ?? item.id;
    onChange({ ...block, artistSlug: slug });
    // Siembra el label al instante (no parpadear al slug).
    setHydrated({ slug, name: item.name, avatarUrl: item.avatarUrl });
    setShowResults(false);
    setQuery('');
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor={`artist-search-${block.id}`}>Artista</Label>
        {selectedSlug && !showResults ? (
          <div className="flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm">
            {selectedArtist?.avatarUrl && (
              <img
                src={selectedArtist.avatarUrl}
                alt=""
                className="size-6 rounded-full object-cover"
              />
            )}
            <span className="flex-1 font-medium">{selectedArtist?.name ?? selectedSlug}</span>
            <button
              type="button"
              onClick={() => {
                onChange({ ...block, artistSlug: '' });
                setQuery('');
              }}
              className="text-xs text-muted-foreground underline hover:text-foreground"
            >
              Cambiar
            </button>
          </div>
        ) : (
          <div className="relative">
            <Input
              id={`artist-search-${block.id}`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar artista por nombre…"
              autoComplete="off"
              onFocus={() => results.length > 0 && setShowResults(true)}
              onBlur={() => setTimeout(() => setShowResults(false), 200)}
            />
            {searching && (
              <p className="mt-1 text-xs text-muted-foreground">Buscando…</p>
            )}
            {showResults && results.length > 0 && (
              <ul className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-md border bg-background shadow-lg">
                {results.map((a) => (
                  <li
                    key={a.id}
                    onMouseDown={() => handleSelect(a)}
                    className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-muted"
                  >
                    {a.avatarUrl && (
                      <img
                        src={a.avatarUrl}
                        alt=""
                        className="size-6 rounded-full object-cover"
                      />
                    )}
                    <span>{a.name}</span>
                  </li>
                ))}
              </ul>
            )}
            {showResults && query.trim().length >= 2 && results.length === 0 && !searching && (
              <p className="mt-1 text-xs text-muted-foreground">Sin resultados</p>
            )}
          </div>
        )}
      </div>
      {selectedSlug && (
        <p className="text-xs text-muted-foreground">
          Slug: <code>{selectedSlug}</code>
        </p>
      )}
    </div>
  );
}
